import {Col, css, DSIconButton, DSIconClose, DSIconFullscreen, DSIconTrash, Row} from "@cdx/ds";
import type {History, Location} from "history";
import * as Y from "yjs";
import {hasPermissionToModifyVisionBoard} from "../../lib/permissions";
import {VisionBoard, VisionBoardId} from "../../cdx-models/VisionBoard";
import {Root} from "../../cdx-models/Root";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import useSyncedVisionBoardDoc, {buildTreeStore, BushEntry} from "./useVisionBoardData";
import CanvasNode from "./widgets/CanvasNode";
import {PosRef, setInitialPosition, VisionBoardBag, AwarenessStore} from "./canvas-utils";
import {useDropTarget} from "../../lib/hooks/useFileDrop";
import {handleCanvasFileDrop} from "./process-image";
import messenger from "../../lib/messenger";
import {CanvasDropOverlay} from "./widgets/ImageWidget";
import {ArrowDef, CanvasEdge, RenderTempEdge} from "./CanvasEdge";
import CanvasActionButtons from "./CanvasActionButtons";
import MultiSelectBox from "./MultiSelectBox";
import {cardUrlState, createArenaContext} from "../../layouts/arena-layout/createArenaContext";
import ShowLeftSidebar from "../../components/ShowLeftSidebar";
import OpenCard from "../card-container/OpenCard";
import Cursors from "./Cursors";
import {VisionBoardNodeId} from "../../cdx-models/utils/extended-fields";
import {MyBoundingBoxStore, useBBox} from "./useBBox";
import {useGrabToPan, useWheel} from "./useCanvasNavigation";
import {useCopyPaste} from "./clipboard-utils";
import {debounce} from "../../lib/utils";
import {getLocalStorageVal, setLocalStorageVal} from "../../lib/storage";
import {TooltipForChild, useEsc} from "@cdx/common";
import {useIsFullScreen} from "../../lib/hooks/useFullscreen";
import {CardId} from "../../cdx-models/Card";
import {api} from "../../lib/api";
import FilterBar from "./FilterBar";
import confirm from "../../lib/confirm";
import VisionBoardPicker from "./VisionBoardPicker";

const debouncedPositionSetter = debounce(
  (posRef: PosRef, boardId: VisionBoardId, bboxStore: MyBoundingBoxStore) => {
    const key = `last-board-position-${boardId}` as "last-board-position-[boardId]";
    const viewport = bboxStore.getSnapshot();
    const centerVal = {
      x: Math.round(viewport.minX + (viewport.maxX - viewport.minX) / 2),
      y: Math.round(viewport.minY + (viewport.maxY - viewport.minY) / 2),
      scale: posRef.current.scale,
    };
    setLocalStorageVal(key, centerVal);
  },
  100
);

const positionCanvasFn = (
  canvasNodeRef: React.RefObject<HTMLDivElement>,
  posRef: PosRef,
  bboxStore: MyBoundingBoxStore
) => {
  const n = canvasNodeRef.current!;
  n.style.transform = `scale(${posRef.current.scale}) translate(${posRef.current.x}px, ${posRef.current.y}px)`;
  n.style.setProperty("--canvas-scale", `${posRef.current.scale}`);
  bboxStore.boxUpdated();
};

const useEntries = (bboxStore: MyBoundingBoxStore, bag: VisionBoardBag): BushEntry[] => {
  const visibleEntries = bag.store.useEntries(bboxStore);
  const visibleIds = new Set(visibleEntries.map((e) => e.id));
  const nonVisibleSelectedIds = bag.selectionStore.useSelectedNodes((idSet) =>
    [...idSet].filter((id) => !visibleIds.has(id))
  );
  if (nonVisibleSelectedIds.length > 0) {
    return [
      ...visibleEntries,
      ...nonVisibleSelectedIds.map((id) => bag.store.getNode(id)!).filter(Boolean),
    ];
  } else {
    return visibleEntries;
  }
};

const vbRoutes = (history: History, rootUrl: string, rootSearch: string) => {
  const getCardUrl = ({card, targetPanel, resolvableId, cardContainerKey}: any) => ({
    pathname: rootUrl,
    search: rootSearch,
    state: {
      vbShowCardId: card.id,
      ...cardUrlState({targetPanel, resolvableId, cardContainerKey}),
    },
  });
  return {
    getActiveCard: (location: Location<{vbShowCardId?: CardId}>) => {
      if (location.state && location.state.vbShowCardId) {
        return api.getModel({modelName: "card", id: location.state.vbShowCardId});
      } else {
        return null;
      }
    },
    getCardUrl,
    goToCard: ({cardId, targetPanel, resolvableId, cardContainerKey}: any) => {
      history.push(getCardUrl({card: {id: cardId}, targetPanel, resolvableId, cardContainerKey}));
    },
    rootPath: "/",
    getRootUrl: () => rootUrl,
    stateBased: true,
  };
};

const useBoardArenaContext = (board: VisionBoard, location: Location, history: History) => {
  return createArenaContext({
    type: "visionBoard",
    shortcutCategory: "Vision Board",
    model: board,
    routes: vbRoutes(history, location.pathname, location.search),
  });
};

type VisionBoardCanvasWithDocProps = {
  doc: Y.Doc;
  canModify: boolean;
  board: VisionBoard;
  history: History;
  location: Location<any>;
  root: Root;
  awarenessStore: AwarenessStore;
  onClose: () => void;
};

const VisionBoardCanvasWithDoc = ({
  doc,
  canModify,
  board,
  history,
  location,
  root,
  awarenessStore,
  onClose,
}: VisionBoardCanvasWithDocProps) => {
  const meId = root.loggedInUser.$meta.get("id", null);
  const boardId = board.$meta.get("id", null);
  const [store] = useState(() => buildTreeStore(doc));
  useEffect(() => {
    return store.unsubscribe;
  }, [store.unsubscribe]);
  const canvasNodeRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const posRef: PosRef = useRef({x: 0, y: 0, scale: 1, canvasOffset: [0, 0]});
  useEffect(() => {}, []);
  const {bboxStore, measureRef} = useBBox(posRef, wrapperRef);

  const positionCanvas = () => {
    positionCanvasFn(canvasNodeRef, posRef, bboxStore);
    if (boardId) debouncedPositionSetter(posRef, boardId, bboxStore);
  };

  const onCloseSidebar = () => {
    history.push({
      ...location,
      state: {...location.state, vbShowCardId: undefined},
    });
  };

  const {renderSelectionBox, isSelecting, handlers, selectionStore} = useGrabToPan(
    posRef,
    positionCanvas,
    wrapperRef,
    (idSet: Set<VisionBoardNodeId>) => {
      if (meId) awarenessStore.setSelectionIds(meId, [...idSet]);
    }
  );
  useWheel(posRef, positionCanvas, wrapperRef);

  const bag: VisionBoardBag = {
    posRef,
    store,
    isSelecting,
    positionCanvas,
    canvasMeasureRef: measureRef,
    visionBoard: board,
    arenaCtx: useBoardArenaContext(board, location, history),
    awarenessStore,
    selectionStore,
    root,
    bboxStore,
    showVisionBoardFilter: (id) => {
      history.push({
        ...location,
        state: id
          ? {
              filterNodeId: id,
            }
          : {},
      });
    },
    shownVisionBoardFilter: location.state?.filterNodeId ?? null,
  };
  const entries = useEntries(bboxStore, bag);

  const getSidebarContent = () => {
    if (bag.shownVisionBoardFilter) {
      return {
        element: <FilterBar bag={bag} id={bag.shownVisionBoardFilter} />,
        childKey: bag.shownVisionBoardFilter,
      };
    }
    const activeCard = bag.arenaCtx.routes.getActiveCard(location);
    if (!activeCard) return null;

    return {
      element: (
        <OpenCard
          card={activeCard}
          arenaCtx={bag.arenaCtx}
          history={history}
          location={location}
          onClose={onCloseSidebar}
          root={root}
        />
      ),
      childKey: location.state.vbShowCardId,
    };
  };

  useLayoutEffect(() => {
    const [[x, y], scale] = setInitialPosition({store, canvasMeasureRef: measureRef, boardId});
    posRef.current.x = -x;
    posRef.current.y = -y;
    posRef.current.scale = scale;
    positionCanvasFn(canvasNodeRef, posRef, bboxStore);
  }, [store, bboxStore, measureRef, boardId]);

  const {dropTargetProps, isOver} = useDropTarget({
    onDrop: async (files) => {
      const res = await handleCanvasFileDrop(files[0], bag);
      if (!res.ok) {
        messenger.send(res.message, {type: "error"});
      }
    },
  });

  useCopyPaste(bag);

  const [isFullScreen, toggleFullscreen] = useIsFullScreen();

  const handleDelete = () =>
    confirm({
      title: "Delete Vision Board",
      content: "This cannot be undone",
      confirmLabel: "Yes, delete",
    }).then((ok) => {
      if (ok) {
        api.mutate.visionBoard.deleteBoard({id: bag.visionBoard.id}).then(onClose);
      }
    });

  return (
    <div
      className={css({
        py: "12px",
        px: "12px",
        width: "100%",
        flex: "auto",
        sp: "24px",
        position: "relative",
        overflow: "hidden",
        colorTheme: "gray550",
        bg: "foreground",
        userSelect: "none",
      })}
      ref={wrapperRef}
      {...handlers}
      {...dropTargetProps}
    >
      {isOver && <CanvasDropOverlay />}
      <Col
        absolute
        top="0"
        left="0"
        width="100%"
        height="100%"
        style={{transformOrigin: "0 0"}}
        ref={canvasNodeRef}
      >
        <Col flex="auto" relative>
          <ArrowDef />
          {entries.map((entry) =>
            entry.type === "node" ? (
              <CanvasNode key={entry.id} node={entry} bag={bag} />
            ) : (
              <CanvasEdge key={entry.id} edge={entry} bag={bag} />
            )
          )}
        </Col>
        <RenderTempEdge bag={bag} />
        {renderSelectionBox(bag)}
        {!isSelecting && <MultiSelectBox bag={bag} />}
        {meId && <Cursors meId={meId} bag={bag} />}
      </Col>
      <Row sp="12px" align="center">
        <VisionBoardPicker bag={bag} />
        {canModify && <CanvasActionButtons bag={bag} />}
      </Row>
      <ShowLeftSidebar onClose={onCloseSidebar} content={getSidebarContent()} />
      <Row absolute top="0" right="0" sp="8px" pt="8px" pr="8px">
        {!bag.visionBoard.isDeleted && (
          <DSIconButton
            icon={<DSIconTrash />}
            variant="tertiary"
            onClick={handleDelete}
            skipSuccessIcon
          />
        )}
        <TooltipForChild tooltip="Full Screen" placement="left">
          <DSIconButton
            icon={<DSIconFullscreen />}
            variant="tertiary"
            onClick={toggleFullscreen}
            size="sm"
            active={isFullScreen}
            negatePadding
          />
        </TooltipForChild>
        <DSIconButton icon={<DSIconClose />} variant="tertiary" onClick={onClose} />
      </Row>
    </div>
  );
};

type VisionBoardProps = {
  board: VisionBoard;
  root: Root;
  onClose: () => void;
  location: Location;
  history: History;
};
const VisionBoardCanvas = (props: VisionBoardProps) => {
  const {board, root, location, history, onClose} = props;
  const boardId = board.$meta.get("id", null);
  const docState = useSyncedVisionBoardDoc(boardId);
  const canModify = !board.isDeleted && hasPermissionToModifyVisionBoard(root, board);
  useEsc(onClose);

  useEffect(() => {
    if (!boardId) return;
    const list = getLocalStorageVal("last-visited-vision-boards") ?? [];
    const nextList = [boardId, ...list.filter((id) => id !== boardId).slice(0, 5)];
    setLocalStorageVal("last-visited-vision-boards", nextList);
  }, [boardId]);

  return (
    docState && (
      <VisionBoardCanvasWithDoc
        doc={docState.doc}
        awarenessStore={docState.awarenessStore}
        canModify={Boolean(canModify)}
        board={board}
        history={history}
        location={location}
        root={root}
        onClose={onClose}
      />
    )
  );
};

export default VisionBoardCanvas;
